import React, { useEffect, useState } from "react";
import {
    Alert,
    Box,
    Chip,
    Collapse,
    IconButton,
    Stack,
    Typography,
} from "@mui/material";
import {
    Close as CloseIcon,
    Email as EmailIcon,
    WhatsApp as WhatsAppIcon,
    Business as BusinessIcon,
} from "@mui/icons-material";
import Cookies from "js-cookie";
import axiosInstance from "../../api/axiosInstance";
import base_url from "../../constants";

const MESSAGING_ROLES = [
    "EMAIL",
    "EMAIL-ADMIN",
    "WB-ADMIN",
    "WB-USER",
    "WB-AGENT",
    "WB-CHAT",
    "PERSONAL",
];

const SERVICE_META = {
    email: {
        label: "Email",
        icon: <EmailIcon sx={{ fontSize: 16 }} />,
    },
    whatsapp_business: {
        label: "WhatsApp Business",
        icon: <BusinessIcon sx={{ fontSize: 16 }} />,
    },
    personal_whatsapp: {
        label: "Personal WhatsApp",
        icon: <WhatsAppIcon sx={{ fontSize: 16 }} />,
    },
};

const STATUS_SEVERITY = {
    healthy: "success",
    paused: "warning",
    restricted: "error",
    subscription_expired: "error",
    inactive: "warning",
    disconnected: "warning",
    unhealthy: "error",
    initiated: "info",
    not_configured: "info",
    no_messaging_services: "info",
};

const STATUS_LABEL = {
    healthy: "Healthy",
    paused: "Paused",
    restricted: "Restricted",
    subscription_expired: "Subscription expired",
    inactive: "Inactive",
    disconnected: "Disconnected",
    unhealthy: "Unhealthy",
    initiated: "Setup incomplete",
    not_configured: "Not configured",
};

const hasMessagingRole = () => {
    const roles = (Cookies.get("userRole") || "")
        .split(",")
        .map((r) => r.trim())
        .filter(Boolean);
    return roles.some((r) => MESSAGING_ROLES.includes(r));
};

const formatStatus = (status) =>
    STATUS_LABEL[status] ||
    String(status || "")
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());

/** Poll every 7 minutes (within the 5–10 min window). */
const REFRESH_INTERVAL_MS = 7 * 60 * 1000;

const MessagingAccountHealthBanner = () => {
    const [health, setHealth] = useState(null);
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        if (!hasMessagingRole()) return undefined;

        let cancelled = false;

        const fetchHealth = async () => {
            try {
                const res = await axiosInstance.get(
                    `${base_url}/messaging/account-health`
                );
                if (!cancelled && res?.data?.success) {
                    setHealth(res.data.data || null);
                }
            } catch {
                // Fail silently — do not block the app on health check
            }
        };

        // After login / when shell mounts
        fetchHealth();

        const intervalId = setInterval(fetchHealth, REFRESH_INTERVAL_MS);

        const onVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                fetchHealth();
            }
        };
        document.addEventListener("visibilitychange", onVisibilityChange);

        return () => {
            cancelled = true;
            clearInterval(intervalId);
            document.removeEventListener("visibilitychange", onVisibilityChange);
        };
    }, []);

    if (dismissed || !health) return null;

    const services = health.services || {};
    const entries = Object.entries(services).filter(
        ([, svc]) => svc && svc.account_status && svc.account_status !== "healthy"
    );

    if (entries.length === 0 && health.overall_status === "healthy") {
        return null;
    }

    const issues =
        entries.length > 0
            ? entries
            : health.overall_status && health.overall_status !== "healthy"
              ? [
                    [
                        "overall",
                        {
                            account_status: health.overall_status,
                            message: "One or more messaging services need attention",
                        },
                    ],
                ]
              : [];

    if (issues.length === 0) return null;

    const topSeverity =
        STATUS_SEVERITY[health.overall_status] ||
        STATUS_SEVERITY[issues[0][1].account_status] ||
        "warning";

    return (
        <Collapse in={!dismissed}>
            <Box sx={{ px: { xs: 2, sm: 3 }, pt: 1.5, pb: 0, flexShrink: 0 }}>
                <Alert
                    severity={topSeverity === "success" ? "info" : topSeverity}
                    action={
                        <IconButton
                            aria-label="dismiss"
                            color="inherit"
                            size="small"
                            onClick={() => setDismissed(true)}
                        >
                            <CloseIcon fontSize="inherit" />
                        </IconButton>
                    }
                    sx={{
                        alignItems: "flex-start",
                        borderRadius: 2,
                        "& .MuiAlert-message": { width: "100%" },
                    }}
                >
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
                        Messaging account health
                    </Typography>
                    <Stack spacing={1}>
                        {issues.map(([key, svc]) => {
                            const meta = SERVICE_META[key] || {
                                label: key === "overall" ? "Account" : key,
                                icon: null,
                            };
                            const severity =
                                STATUS_SEVERITY[svc.account_status] || "warning";
                            return (
                                <Box
                                    key={key}
                                    sx={{
                                        display: "flex",
                                        alignItems: "flex-start",
                                        gap: 1,
                                        flexWrap: "wrap",
                                    }}
                                >
                                    <Chip
                                        icon={meta.icon || undefined}
                                        label={meta.label}
                                        size="small"
                                        sx={{ height: 24, fontWeight: 600 }}
                                    />
                                    <Chip
                                        label={formatStatus(svc.account_status)}
                                        size="small"
                                        color={
                                            severity === "error"
                                                ? "error"
                                                : severity === "warning"
                                                  ? "warning"
                                                  : severity === "info"
                                                    ? "info"
                                                    : "default"
                                        }
                                        sx={{ height: 24, fontWeight: 600 }}
                                    />
                                    {svc.message && (
                                        <Typography
                                            variant="body2"
                                            sx={{ color: "text.secondary", flex: 1, minWidth: 180 }}
                                        >
                                            {svc.message}
                                            {svc.send_paused_until
                                                ? ` · Resume after ${new Date(
                                                      svc.send_paused_until
                                                  ).toLocaleString("en-IN")}`
                                                : ""}
                                        </Typography>
                                    )}
                                </Box>
                            );
                        })}
                    </Stack>
                </Alert>
            </Box>
        </Collapse>
    );
};

export default MessagingAccountHealthBanner;
